import { Action } from 'redux';
import { SagaIterator } from 'redux-saga';
import { all, call, debounce, fork, join, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { Request } from '../../api/request';
import { API_URL, ENDPOINTS } from '../../constants/api';
import { DEFAULT_ERROR_MESSAGE, DEFAULT_ERROR_TITLE } from '../../constants/errors';
import {
  MARKETPLACE_CACHE_HEADER,
  MARKETPLACE_DETAILS_BATCH_SIZE,
  MARKETPLACE_FILTER_DEBOUNCE_MS,
  MARKETPLACE_PAGE_SIZE,
  MARKETPLACE_SEARCH_MAX_LENGTH,
} from '../../constants/marketplace';
import { IRequestParams, IResponse } from '../../types/http';
import {
  IMarketplaceCatalogItem,
  IMarketplaceCatalogRequestBody,
  IMarketplaceCatalogResponse,
  IMarketplaceMetadataResponse,
  IMarketplaceProductDetails,
  IMarketplaceProductDetailsResponse,
} from '../../types/marketplace';
import {
  ActionsEnum,
  IAction,
  IError,
  IMarketplaceCatalogRequestPayload,
  IMarketplaceHydrateItemsPayload,
} from '../../types/store';
import { canAffordCredits, isProductInCart } from '../../util/credits';
import { chunk } from '../../util/helpers';
import {
  buildCartLineFromDetails,
  getSelectableVariants,
  getSupplierLock,
  hasSingleVariant,
  isOtherSupplier,
  normalizeCatalogItem,
  normalizeMetadata,
  normalizeProductDetails,
} from '../../util/marketplace';
import * as MarketplaceActions from '../actions/marketplace';
import { selectInitKeys } from '../selectors/keys';
import {
  selectCatalogBudget,
  selectIsMetadataLoaded,
  selectMarketplaceCacheValue,
  selectMarketplaceCart,
  selectMarketplaceCatalogItem,
  selectMarketplaceFilters,
  selectMarketplaceNextCursor,
} from '../selectors/marketplace';
import { prepareAuthHeadersSaga } from './helpers';

const UNEXPECTED_ERROR: IError = { title: DEFAULT_ERROR_TITLE, message: DEFAULT_ERROR_MESSAGE };

type TMarketplaceRequestParams = Pick<IRequestParams, 'endpoint' | 'method' | 'body'>;

interface IMarketplaceCachedResponse {
  cache_value?: string;
}

const normalizeCatalogItems = (items: IMarketplaceCatalogResponse['items']): IMarketplaceCatalogItem[] =>
  items.map(normalizeCatalogItem).filter((item): item is IMarketplaceCatalogItem => item !== null);

// The claim identifiers authorize every marketplace request, as query params like the form-data
// endpoints. Exactly one flow: sending both is a 400.
function* getClaimQueryParamsSaga(): SagaIterator {
  const { order_id, key, one_link_id } = yield select(selectInitKeys);

  return one_link_id ? { one_link_id } : { order_id: order_id ?? '', key: key ?? '' };
}

// Auth headers plus the cache token from an earlier response, which spares the server the budget
// lookup. A stale or foreign token is ignored server-side and replaced, never rejected.
function* prepareMarketplaceHeadersSaga(): SagaIterator {
  const headers = yield call(prepareAuthHeadersSaga);
  const cacheValue = yield select(selectMarketplaceCacheValue);

  return cacheValue ? { ...headers, [MARKETPLACE_CACHE_HEADER]: cacheValue } : headers;
}

function* marketplaceRequestSaga(params: TMarketplaceRequestParams): SagaIterator {
  const headers = yield call(prepareMarketplaceHeadersSaga);
  const queryParams = yield call(getClaimQueryParamsSaga);
  const response: IResponse<IMarketplaceCachedResponse | IError> = yield call<any>(Request, {
    ...params,
    headers,
    queryParams,
  });

  // Present only when the token sent was missing or unusable; absence means keep the current one.
  const cacheValue = response.ok ? (response.body as IMarketplaceCachedResponse)?.cache_value : undefined;

  if (cacheValue) {
    yield put(MarketplaceActions.marketplaceSetCacheValueAction(cacheValue));
  }

  return response;
}

function* metadataSaga(): SagaIterator {
  try {
    const response: IResponse<IMarketplaceMetadataResponse | IError> = yield call(marketplaceRequestSaga, {
      endpoint: `${API_URL}${ENDPOINTS.marketplaceMetadata}`,
      method: 'GET',
    });

    if (response.ok) {
      yield put(
        MarketplaceActions.marketplaceMetadataSuccessAction(
          normalizeMetadata(response.body as IMarketplaceMetadataResponse),
        ),
      );
    }
  } catch (error) {
    // Swallowed on purpose: the catalog renders without the filter row.
  }
}

function* catalogSaga(action: IAction<IMarketplaceCatalogRequestPayload>): SagaIterator {
  const { isLoadMore } = action.payload || {};

  try {
    const filters = yield select(selectMarketplaceFilters);
    const cursor: string | null = isLoadMore ? yield select(selectMarketplaceNextCursor) : null;
    const budget: number | null = yield select(selectCatalogBudget);
    const isMetadataLoaded: boolean = yield select(selectIsMetadataLoaded);

    // Once per claim, and sequential on purpose: the metadata response yields the cache token the
    // catalog call reuses. A failed fetch leaves it unloaded, so the next request retries it.
    if (!isLoadMore && !isMetadataLoaded) {
      yield call(metadataSaga);
    }

    const body: IMarketplaceCatalogRequestBody = {
      search_query: filters.query.trim().slice(0, MARKETPLACE_SEARCH_MAX_LENGTH),
      categories: filters.categories,
      brands: filters.brands,
      collections: filters.collections,
      // The server does the fitting from what is left in the wallet; null asks for the full wallet.
      remaining_budget: budget,
      page_size: MARKETPLACE_PAGE_SIZE,
      ...(cursor ? { cursor } : {}),
    };

    const response: IResponse<IMarketplaceCatalogResponse | IError> = yield call(marketplaceRequestSaga, {
      endpoint: `${API_URL}${ENDPOINTS.marketplaceCatalog}`,
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      yield put(MarketplaceActions.marketplaceCatalogFailureAction((response.body as IError) || UNEXPECTED_ERROR));

      return;
    }

    const { items = [], has_more, next_cursor } = response.body as IMarketplaceCatalogResponse;

    // has_more, not page fill, says whether to load more: a short page is normal.
    yield put(
      MarketplaceActions.marketplaceCatalogSuccessAction({
        items: normalizeCatalogItems(items),
        nextCursor: has_more && next_cursor ? next_cursor : null,
        budget,
        isLoadMore,
      }),
    );
  } catch (error) {
    yield put(MarketplaceActions.marketplaceCatalogFailureAction(UNEXPECTED_ERROR));
  }
}

// Returns null on a failed response; throws only on a network error.
function* fetchProductDetailsSaga(productId: string): SagaIterator {
  const response: IResponse<IMarketplaceProductDetailsResponse | IError> = yield call(marketplaceRequestSaga, {
    endpoint: `${API_URL}${ENDPOINTS.marketplaceProduct(productId)}`,
    method: 'GET',
  });
  const product = response.ok ? (response.body as IMarketplaceProductDetailsResponse).product : null;

  return product ? normalizeProductDetails(product) : null;
}

function* productSaga(action: IAction<string>): SagaIterator {
  const productId = action.payload;

  if (!productId) {
    return;
  }

  try {
    const details = yield call(fetchProductDetailsSaga, productId);

    if (details) {
      yield put(MarketplaceActions.marketplaceProductSuccessAction(details));
    } else {
      yield put(MarketplaceActions.marketplaceProductFailureAction({ productId, error: UNEXPECTED_ERROR }));
    }
  } catch (error) {
    yield put(MarketplaceActions.marketplaceProductFailureAction({ productId, error: UNEXPECTED_ERROR }));
  }
}

const isProductSettled = (productId: string) => (action: Action<string>) => {
  const { type, payload } = action as IAction<IMarketplaceProductDetails | { productId: string }>;

  if (type === ActionsEnum.MarketplaceProductSuccess) {
    return (payload as IMarketplaceProductDetails).product_id === productId;
  }

  return type === ActionsEnum.MarketplaceProductFailure && (payload as { productId: string }).productId === productId;
};

// Add from a catalog card. The catalog payload carries no variants, so the decision waits on
// details: a single-variant product goes straight to the cart, anything else hands off to the
// sidebar. The fetch itself runs through the product watcher so the card and the sidebar share
// one request and one status.
function* addDirectSaga(action: IAction<string>): SagaIterator {
  const productId = action.payload;

  if (!productId) {
    return;
  }

  yield put(MarketplaceActions.marketplaceProductRequestAction(productId));
  const settled: IAction<IMarketplaceProductDetails | IError> = yield take(isProductSettled(productId));

  if (settled.type !== ActionsEnum.MarketplaceProductSuccess) {
    return;
  }

  const details = settled.payload as IMarketplaceProductDetails;
  const cart = yield select(selectMarketplaceCart);

  if (isProductInCart(productId, cart)) {
    return;
  }

  const catalogItem = yield select(selectMarketplaceCatalogItem(productId));
  const supplierCode = details.supplierCode ?? catalogItem?.supplierCode;

  // Before the budget check: a quick add never replaces picks, the sidebar explains and offers it.
  if (isOtherSupplier(supplierCode, getSupplierLock(cart)) || !hasSingleVariant(details)) {
    yield put(MarketplaceActions.marketplaceOpenDetailsAction(productId));

    return;
  }

  const [variant] = getSelectableVariants(details);
  const line = buildCartLineFromDetails(details, variant, catalogItem);

  // Re-checked here, not on the card: the cart may have changed while details were in flight. A
  // cart with picks to free hands the item to the swap flow, which never checks it against the
  // whole wallet: a line priced above it stays pending forever (needs a product decision).
  if (!canAffordCredits(line.creditPrice, cart)) {
    if (cart.length) {
      yield put(MarketplaceActions.marketplaceSwapStartAction(line));
    } else {
      yield put(MarketplaceActions.marketplaceOpenDetailsAction(productId));
    }

    return;
  }

  yield put(MarketplaceActions.marketplaceAddToCartAction(line));
}

// Never throws: `all` has Promise.all semantics, so one rejection would cancel the rest of the
// wave. Converting failure into an action keeps a bad product to its own card.
function* hydrateOneProductSaga(productId: string): SagaIterator {
  try {
    const details: IMarketplaceProductDetails | null = yield call(fetchProductDetailsSaga, productId);

    if (details) {
      yield put(MarketplaceActions.marketplaceHydrateItemSuccessAction({ productId, details }));
    } else {
      yield put(MarketplaceActions.marketplaceHydrateItemFailureAction({ productId }));
    }
  } catch (error) {
    yield put(MarketplaceActions.marketplaceHydrateItemFailureAction({ productId }));
  }
}

// Waves of MARKETPLACE_DETAILS_BATCH_SIZE: each wave runs concurrently and settles before the
// next starts, so options appear in groups rather than all at once at the end.
function* hydrateMarketplaceDetailsSaga(action: IAction<IMarketplaceHydrateItemsPayload>): SagaIterator {
  const { productIds } = action.payload || { productIds: [] };

  for (const batch of chunk(productIds, MARKETPLACE_DETAILS_BATCH_SIZE)) {
    const tasks = yield all(batch.map((productId: string) => fork(hydrateOneProductSaga, productId)));
    yield join(tasks);
  }
}

function* filtersSaga(): SagaIterator {
  yield put(MarketplaceActions.marketplaceCatalogRequestAction());
}

// While "Fits budget" is on the list is scoped to the remaining wallet, so every cart change
// invalidates it. Off, the cart has no bearing on what the catalog returns.
function* cartChangeSaga(): SagaIterator {
  const filters = yield select(selectMarketplaceFilters);

  if (filters.fitsBudget) {
    yield put(MarketplaceActions.marketplaceCatalogRequestAction());
  }
}

const sagas = {
  *watchMarketplaceCatalog() {
    yield takeLatest(ActionsEnum.MarketplaceCatalogRequest, catalogSaga);
  },
  // takeEvery: opening one product must not cancel the fetch a card's direct add is waiting on
  // for another, which would leave that product checking forever.
  *watchMarketplaceProduct() {
    yield takeEvery(ActionsEnum.MarketplaceProductRequest, productSaga);
  },
  *watchMarketplaceAddDirect() {
    yield takeLatest(ActionsEnum.MarketplaceAddDirectRequest, addDirectSaga);
  },
  // takeEvery: a retry for one product must not cancel a wave still running for the others.
  *watchMarketplaceHydrateItems() {
    yield takeEvery(ActionsEnum.MarketplaceHydrateItemsRequest, hydrateMarketplaceDetailsSaga);
  },
  *watchMarketplaceFilters() {
    yield debounce(MARKETPLACE_FILTER_DEBOUNCE_MS, ActionsEnum.MarketplaceSetFilters, filtersSaga);
  },
  // Debounced so a quick add-then-remove settles into one request.
  *watchMarketplaceCartChanges() {
    yield debounce(
      MARKETPLACE_FILTER_DEBOUNCE_MS,
      [
        ActionsEnum.MarketplaceAddToCart,
        ActionsEnum.MarketplaceRemoveFromCart,
        ActionsEnum.MarketplaceClearCart,
        ActionsEnum.MarketplaceReplaceCart,
        ActionsEnum.MarketplaceRestoreCart,
      ],
      cartChangeSaga,
    );
  },
};

export default sagas;
